import React, { forwardRef, HTMLProps, useCallback, useMemo } from 'react';
import { StringifiableRecord, stringifyUrl } from 'query-string';

import { useSmartTo } from 'hooks/useSmartNavigate';
import { CONFIRMATION_TEST_ID } from 'routes/Confirmation/const';
import { getHeapUserId } from 'util/Analytics';

type AppLinkProps = Omit<HTMLProps<HTMLAnchorElement>, 'href'> & {
  to?: string;
  query?: StringifiableRecord;
};

export const AppLink = forwardRef<HTMLAnchorElement, AppLinkProps>(function AppLink({
  to, query: queryParams, role, target, onClick, children, rel, ...props
}, ref) {
  const format = useSmartTo();

  const heapUserId = getHeapUserId();

  const query = useMemo(() => ({ ...queryParams, heapUserId }), [heapUserId, queryParams]);

  const path = useMemo(() => {
    const url = format(`~/app${to ? `/${to.split('/').filter(s => !!s).join('/')}` : ''}`);

    return stringifyUrl({ url, query });
  }, [format, to, query]);

  const wrappedOnClick: React.MouseEventHandler<HTMLAnchorElement> = useCallback(e => {
    e.preventDefault();

    window.open(path);

    onClick?.(e);
  }, [path, onClick]);

  const targetValue = useMemo(() => target ?? '_blank', [target]);

  const relValue = useMemo(() => rel ?? (targetValue === '_blank' ? 'noreferrer' : undefined), [rel, targetValue]);

  // security risk mitigated by memo above
  /* eslint-disable react/jsx-no-target-blank */
  return (
    <a
      {...props}
      role={role ?? 'link'}
      ref={ref}
      target={targetValue}
      rel={relValue}
      href={path}
      onClick={wrappedOnClick}
      data-testid={CONFIRMATION_TEST_ID.cmaHandoff}
    >
      {children}
    </a>
  );
  /* eslint-enable react/jsx-no-target-blank */
});
